import { Check, Warning } from "@phosphor-icons/react"
import { cva, VariantProps } from "class-variance-authority"
import DOMPurify from "dompurify"
import { cn } from "@/lib/utils"

export const tagVariants = cva(
  "flex w-fit items-center rounded-sm px-1 py-0.5 text-xs leading-4 font-normal tracking-[0.25px] not-italic",
  {
    variants: {
      variant: {
        neutral: "bg-surface-subdued text-on-surface",
        success: "bg-success-surface-subdued text-on-success-surface",
        warning: "bg-warning-surface-subdued text-on-warning-surface",
        critical: "bg-critical-surface text-on-critical-surface",
        "most-critical": "bg-critical text-on-critical",
        attention: "bg-ochre-surface text-on-ochre-surface",
      },
    },
    defaultVariants: {
      variant: "neutral",
    },
  },
)

export type TagVariant = VariantProps<typeof tagVariants>["variant"]

interface TagProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: TagVariant
  label?: string
  hasIcon?: boolean
  icon?: React.JSX.Element
  visibleTitle?: boolean
}

const Icon = ({ type }: { type: Exclude<NonNullable<TagVariant>, "neutral"> }) => {
  switch (type) {
    case "success":
      return <Check size={16} />
    case "warning":
      return <Warning size={16} />
    case "critical":
      return <Warning size={16} weight="fill" />
    case "most-critical":
      return <Warning size={16} weight="fill" />
    case "attention":
      return null
  }
}

export const Tag = ({
  type,
  label,
  className,
  hasIcon = true,
  icon,
  visibleTitle = true,
  dangerouslySetInnerHTML,
  ...tagProps
}: TagProps &
  (
    | {
        hasIcon?: true
        icon?: React.JSX.Element
      }
    | {
        hasIcon?: false
        icon?: never
      }
  ) &
  (
    | {
        label?: string
        dangerouslySetInnerHTML?: never
      }
    | {
        dangerouslySetInnerHTML?: { __html: string | TrustedHTML }
        label?: never
      }
  )) => {
  // If dangerouslySetInnerHTML is provided, sanitize it
  let sanitizedInnerHTML

  if (dangerouslySetInnerHTML) {
    if (typeof dangerouslySetInnerHTML.__html === "string") {
      sanitizedInnerHTML = {
        __html: DOMPurify.sanitize(dangerouslySetInnerHTML.__html),
      }
    } else {
      sanitizedInnerHTML = {
        __html: dangerouslySetInnerHTML.__html,
      }
    }
  }

  return (
    <div {...tagProps} className={cn(tagVariants({ variant: type }), className)}>
      {hasIcon && (icon ? icon : type && type !== "neutral" && <Icon type={type} />)}
      {(label || dangerouslySetInnerHTML) && (
        <span
          className="truncate px-1"
          title={visibleTitle ? label : ""}
          // oxlint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={sanitizedInnerHTML}>
          {label}
        </span>
      )}
    </div>
  )
}
